import { useState, useEffect } from "react";
import { makeCall, getAllCalls, getRecordingUrl } from "../api/api";
import { useSocket } from "../hooks/useSocket";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import VirtualCallDetailList from "../components/VirtualCallDetailList";

function Mvp() {
    const { on, off, isConnected } = useSocket();
    const [numbers, setNumbers] = useState(["+918717914659", "", ""]);
    const [message, setMessage] = useState("");
    const [statuses, setStatuses] = useState({});
    
    // Use infinite scroll for calls with memory limit
    const {
        data: callsData,
        loading: loadingCalls,
        hasMore: hasMoreCalls,
        observerTarget: callsObserverTarget,
        reset: resetCalls
    } = useInfiniteScroll(
        async (page, limit) => {
            const res = await getAllCalls({ params: { page, limit } });
            return res;
        },
        {
            initialPage: 1,
            pageSize: 20,
            enabled: true,
            maxItemsInMemory: 500 // Keep only last 500 items (detailed items use more memory)
        }
    );

    const calls = callsData || [];

    const handleNumberChange = (index, value) => {
        const copy = [...numbers];
        copy[index] = value;
        setNumbers(copy);
    };

    const makeCalls = async () => {
        const toNumbers = numbers.map((n) => n.trim()).filter((n) => n !== "");
        if (toNumbers.length === 0) {
            setMessage("❌ Please enter at least one number.");
            return;
        }
        try {
            setMessage("⏳ Initiating calls...");
            const res = await makeCall(toNumbers);
            const init = {};
            (res.data.calls || []).forEach((c) => {
                init[c.callSid] = "initiated";
            });
            setStatuses((prev) => ({ ...init, ...prev }));
            setMessage(`✅ ${res.data.calls.length} call(s) initiated`);
        } catch (err) {
            setMessage(`❌ ${err.response?.data?.error || err.message}`);
        }
    };

    // Calls are loaded via infinite scroll hook

    // Realtime updates via socket
    useEffect(() => {
        const handleCallStatus = (data) => {
            setStatuses((prev) => ({ ...prev, [data.callSid]: data.status }));
        };

        const handleAllCalls = (records) => {
            // Calls are managed by infinite scroll hook
            // Socket updates can trigger a reset if needed
            resetCalls();
        };

        on("call-status", handleCallStatus);
        on("all-calls", handleAllCalls);

        return () => {
            off("call-status", handleCallStatus);
            off("all-calls", handleAllCalls);
        };
    }, [on, off]);

    return (
        <div className="p-5 font-sans max-w-[1000px] mx-auto">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">📞 Voxipro Voice Agent — MVP</h2>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${isConnected
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                    }`}>
                    <div className={`w-2 h-2 rounded-full inline-block mr-2 ${isConnected ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                    {isConnected ? 'Connected' : 'Disconnected'}
                </div>
            </div>

            <div className="flex items-center justify-between gap-2">
                <div className="flex flex-wrap gap-2">
                    {numbers.map((n, i) => (
                        <input
                            key={i}
                            value={n}
                            onChange={(e) => handleNumberChange(i, e.target.value)}
                            placeholder={`Number ${i + 1} (e.g. +91...)`}
                            className="w-56 p-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    ))}
                </div>
                <button
                    onClick={makeCalls}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 cursor-pointer"
                >
                    Start Calls
                </button>
            </div>

            <div className="mt-2 mb-2">
                <div className="mt-2">
                    <span>{message}</span>
                </div>
            </div>

            <div className="mb-5">
                <h3 className="text-lg font-medium mb-2">📡 Live Call Status</h3>
                {Object.keys(statuses).length === 0 ? (
                    <div className="text-gray-600">No active calls</div>
                ) : (
                    <ul className="space-y-1">
                        {Object.entries(statuses).map(([sid, st]) => (
                            <li key={sid} className="text-sm">
                                <b className="font-mono">{sid}</b> — {st}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="mt-4">
                <h3 className="text-lg font-medium mb-2">📋 Recent Calls</h3>
                <VirtualCallDetailList
                    calls={calls}
                    getRecordingUrl={getRecordingUrl}
                    height={600}
                    itemHeight={400}
                    hasMore={hasMoreCalls}
                    loading={loadingCalls}
                    observerTarget={callsObserverTarget}
                />
            </div>
        </div>
    );
}

export default Mvp;
